import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { lociAmber } from '../../../theme/design-tokens';
import { cltAccentTokens } from '../../../tokens/colors';
import { userIdToGradientAngle } from '../../../utils/hash';
const AVATAR_SIZE = 78;
// National Electric Amber accent (DS v1.1.0). On CLT surfaces the
// page-dominant CTA color is Skyline; the avatar gradient deliberately uses
// the cross-city amber pop alongside the lifted skyline glow.
const AMBER = lociAmber;
const TEAL = cltAccentTokens.skylineGlow;
function computeInitials(name, firstName, lastName) {
    const fromName = name?.trim().split(/\s+/).filter(Boolean) ?? [];
    const first = (firstName?.trim() || fromName[0] || '').charAt(0);
    const last = (lastName?.trim() || (fromName.length > 1 ? fromName[fromName.length - 1] : '')).charAt(0);
    const initials = `${first}${last}`.toUpperCase();
    return initials || '?';
}
function resolveDisplayName(name, firstName, lastName) {
    if (name?.trim())
        return name.trim();
    const joined = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return joined || 'Anonymous';
}
function resolveHandle(handle, firstName) {
    if (handle?.trim())
        return handle.trim();
    if (firstName?.trim())
        return firstName.trim().toLowerCase();
    return 'user';
}
export const AvatarHero = forwardRef(({ userId, firstName, lastName, name, handle, joinedYear, avatarUrl, onSettingsClick, className, style, ...rest }, ref) => {
    const theme = useTheme();
    const angle = userIdToGradientAngle(userId);
    const initials = computeInitials(name, firstName, lastName);
    const displayName = resolveDisplayName(name, firstName, lastName);
    const displayHandle = resolveHandle(handle, firstName);
    const gradient = `linear-gradient(${angle}deg, ${AMBER} 0%, ${TEAL} 100%)`;
    const avatarCommonStyles = {
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: '50%',
        padding: 0,
        border: 'none',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        backgroundImage: avatarUrl ? undefined : gradient,
        color: theme.colors.background,
    };
    const initialsStyles = {
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: 32,
        lineHeight: 1,
        letterSpacing: '0.02em',
        userSelect: 'none',
    };
    const nameStyles = {
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: 28,
        lineHeight: 1.15,
        color: theme.colors.text,
        margin: 0,
        marginTop: 16,
    };
    const captionStyles = {
        fontFamily: theme.typography.fontFamilyMono ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.caption,
        color: theme.colors.textMuted,
        margin: 0,
        marginTop: 4,
        letterSpacing: '0.02em',
    };
    const wrapperStyles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...style,
    };
    const classes = ['focus-within:outline-none', className].filter(Boolean).join(' ');
    return (_jsxs("div", { ref: ref, className: classes, style: wrapperStyles, ...rest, children: [_jsx("button", { type: "button", "aria-label": "Open profile settings", onClick: onSettingsClick, style: avatarCommonStyles, className: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2", children: avatarUrl ? (_jsx("img", { src: avatarUrl, alt: "", style: { width: '100%', height: '100%', objectFit: 'cover' } })) : (_jsx("span", { style: initialsStyles, "aria-hidden": "true", children: initials })) }), _jsx("p", { style: nameStyles, children: displayName }), _jsxs("p", { style: captionStyles, children: ["@", displayHandle, typeof joinedYear === 'number' ? ` · joined ${joinedYear}` : ''] })] }));
});
AvatarHero.displayName = 'AvatarHero';
